/**
 * useDailyStoplightStats
 * ----------------------
 * Reads the encounter stoplight localStorage and returns counts of today's
 * signed/copied/unsigned notes for the header streak chip. Updates reactively
 * via the `writeright:encounter-status-changed` event.
 *
 * Feb 16, 2026 — UX polish #12.
 */
import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'writeright_encounter_status_v1';

const _readMap = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {}; }
  catch { return {}; }
};

const _isToday = (iso) => {
  if (!iso) return false;
  try {
    const t = new Date(iso);
    const now = new Date();
    return t.getFullYear() === now.getFullYear()
      && t.getMonth() === now.getMonth()
      && t.getDate() === now.getDate();
  } catch { return false; }
};

const _computeStats = () => {
  const map = _readMap();
  let signedToday = 0;
  let copiedToday = 0;
  let unsignedToday = 0;
  for (const id of Object.keys(map)) {
    const raw = map[id];
    const entry = (raw && typeof raw === 'object') ? raw : { state: raw };
    if (entry.signed_at && _isToday(entry.signed_at)) signedToday += 1;
    if (entry.copied_at && _isToday(entry.copied_at)) copiedToday += 1;
    if ((entry.state === 'amber' || entry.state === 'yellow') && entry.copied_at && _isToday(entry.copied_at)) {
      unsignedToday += 1;
    }
  }
  return { signedToday, copiedToday, unsignedToday };
};

export const useDailyStoplightStats = () => {
  const [stats, setStats] = useState(_computeStats);
  const refresh = useCallback(() => setStats(_computeStats()), []);
  useEffect(() => {
    const onChange = () => refresh();
    window.addEventListener('writeright:encounter-status-changed', onChange);
    // Refresh every minute to keep relative counts current after midnight rollover
    const intervalId = setInterval(refresh, 60_000);
    return () => {
      window.removeEventListener('writeright:encounter-status-changed', onChange);
      clearInterval(intervalId);
    };
  }, [refresh]);
  return stats;
};

export default useDailyStoplightStats;
